export interface SearchResult {
  id: string;
  title: string;
  description: string;
  url: string;
  category: "Features" | "Solutions" | "Pages" | "Blog" | "API" | "Roadmap";
  tags?: string[];
}

// Features Data
export const features: SearchResult[] = [
  {
    id: "feature-ommni-flow",
    title: "Ommni Flow",
    description: "Powerful workflow automation tool. Automate repetitive tasks, connect systems, and streamline operations without code.",
    url: "/features/ommni-flow",
    category: "Features",
    tags: ["workflow", "automation", "n8n", "shopify flow", "no-code", "triggers", "conditional logic"],
  },
  {
    id: "feature-pim",
    title: "Product Information Management (PIM)",
    description: "Centralized product data management. Manage, enrich, and distribute product information across all channels.",
    url: "/features/pim",
    category: "Features",
    tags: ["pim", "product", "data", "catalog", "enrichment", "multi-channel"],
  },
  {
    id: "feature-dam",
    title: "Digital Asset Management (DAM)",
    description: "Organize and manage all digital assets. Store, search, and distribute images, videos, and media files.",
    url: "/features/dam",
    category: "Features",
    tags: ["dam", "assets", "images", "media", "files", "photos", "videos"],
  },
  {
    id: "feature-dom",
    title: "Distributed Order Management (DOM)",
    description: "Intelligent order routing and fulfillment. Optimize fulfillment across your entire network.",
    url: "/features/dom",
    category: "Features",
    tags: ["dom", "orders", "fulfillment", "routing", "shipping", "bopis"],
  },
  {
    id: "feature-offers",
    title: "Pricing & Offers",
    description: "Dynamic pricing and promotional tools. Create targeted offers and optimize pricing strategies.",
    url: "/features/offers",
    category: "Features",
    tags: ["pricing", "offers", "promotions", "discounts", "sales", "bundles"],
  },
  {
    id: "feature-inventory-page",
    title: "Inventory Management",
    description: "Real-time inventory control across all locations. Track, sync, and optimize your inventory operations.",
    url: "/features/inventory",
    category: "Features",
    tags: ["inventory", "stock", "warehouse", "tracking", "multi-location"],
  },
  {
    id: "feature-1",
    title: "Inventory Management",
    description: "Real-time inventory tracking across all channels",
    url: "/features",
    category: "Features",
    tags: ["inventory", "stock", "real-time", "tracking"],
  },
  {
    id: "feature-2",
    title: "Order Management",
    description: "Streamline order processing and fulfillment",
    url: "/features",
    category: "Features",
    tags: ["orders", "fulfillment", "processing"],
  },
  {
    id: "feature-3",
    title: "Omnichannel Commerce",
    description: "Unified commerce across all sales channels",
    url: "/features",
    category: "Features",
    tags: ["omnichannel", "multi-channel", "unified"],
  },
  {
    id: "feature-4",
    title: "Analytics & Reporting",
    description: "Advanced insights into your commerce operations",
    url: "/features",
    category: "Features",
    tags: ["analytics", "reporting", "insights", "data"],
  },
];

// Solutions Data
export const solutions: SearchResult[] = [
  {
    id: "solution-1",
    title: "E-commerce Solution",
    description: "Complete commerce platform for online retailers",
    url: "/solutions/ecommerce",
    category: "Solutions",
    tags: ["ecommerce", "retail", "online"],
  },
  {
    id: "solution-2",
    title: "Fashion & Apparel",
    description: "Specialized solutions for fashion retailers",
    url: "/solutions/fashion",
    category: "Solutions",
    tags: ["fashion", "apparel", "retail"],
  },
  {
    id: "solution-3",
    title: "Manufacturing",
    description: "Commerce solutions for manufacturers",
    url: "/solutions/manufacturing",
    category: "Solutions",
    tags: ["manufacturing", "production", "b2b"],
  },
  {
    id: "solution-4",
    title: "Wholesale",
    description: "B2B commerce platform for wholesalers",
    url: "/solutions/wholesale",
    category: "Solutions",
    tags: ["wholesale", "b2b", "bulk"],
  },
];

// Pages Data
export const pages: SearchResult[] = [
  {
    id: "page-1",
    title: "Pricing",
    description: "View our transparent pricing plans",
    url: "/pricing",
    category: "Pages",
    tags: ["pricing", "plans", "costs"],
  },
  {
    id: "page-2",
    title: "Features",
    description: "Explore all platform features",
    url: "/features",
    category: "Pages",
    tags: ["features", "capabilities"],
  },
  {
    id: "page-3",
    title: "Contact",
    description: "Get in touch with our team",
    url: "/contact",
    category: "Pages",
    tags: ["contact", "support", "help"],
  },
  {
    id: "page-4",
    title: "Free Trial",
    description: "Start your free trial today",
    url: "/free-trial",
    category: "Pages",
    tags: ["trial", "demo", "start"],
  },
  {
    id: "page-5",
    title: "Blog",
    description: "Latest news and insights",
    url: "/blog",
    category: "Pages",
    tags: ["blog", "news", "insights"],
  },
  {
    id: "page-6",
    title: "Integrations",
    description: "Connect with your favorite tools",
    url: "/integrations",
    category: "Pages",
    tags: ["integrations", "connect", "tools"],
  },
  {
    id: "page-7",
    title: "Team",
    description: "Meet our team",
    url: "/team",
    category: "Pages",
    tags: ["team", "about", "people"],
  },
  {
    id: "page-8",
    title: "Careers",
    description: "Join our team",
    url: "/careers",
    category: "Pages",
    tags: ["careers", "jobs", "hiring"],
  },
  {
    id: "page-9",
    title: "Support",
    description: "Get help from our support team",
    url: "/support",
    category: "Pages",
    tags: ["support", "help", "contact", "assistance"],
  },
  {
    id: "page-10",
    title: "Product Roadmap",
    description: "View our product roadmap and upcoming features",
    url: "/roadmap",
    category: "Pages",
    tags: ["roadmap", "features", "upcoming", "planned", "future", "development"],
  },
];

// Blog Data
export const blogPosts: SearchResult[] = [
  {
    id: "blog-1",
    title: "The Future of Commerce Operations",
    description: "Exploring trends shaping the commerce landscape in 2024",
    url: "/blog",
    category: "Blog",
    tags: ["commerce", "trends", "future", "operations"],
  },
  {
    id: "blog-2",
    title: "Multi-Channel Commerce Best Practices",
    description: "How to succeed across multiple sales channels",
    url: "/blog",
    category: "Blog",
    tags: ["multi-channel", "best practices", "strategy"],
  },
];

// Competitor Comparison Pages
export const competitorPages: SearchResult[] = [
  {
    id: "comp-1",
    title: "Ommni vs Akeneo",
    description: "Compare Ommni's inventory-ready PIM with Akeneo's product content management solution",
    url: "/competitors/akeneo",
    category: "Pages",
    tags: ["competitor", "comparison", "akeneo", "pim", "alternative"],
  },
  {
    id: "comp-2",
    title: "Ommni vs Pimberly",
    description: "See how Ommni's inventory and pricing logic compares to Pimberly's content enrichment",
    url: "/competitors/pimberly",
    category: "Pages",
    tags: ["competitor", "comparison", "pimberly", "pim", "alternative"],
  },
  {
    id: "comp-3",
    title: "Ommni vs Sales Layer",
    description: "Compare Ommni's retailer-focused platform with Sales Layer's supplier catalog management",
    url: "/competitors/sales-layer",
    category: "Pages",
    tags: ["competitor", "comparison", "sales layer", "pim", "alternative"],
  },
  {
    id: "comp-4",
    title: "Ommni vs Salsify",
    description: "Discover why retailers choose Ommni over Salsify for omnichannel commerce",
    url: "/competitors/salsify",
    category: "Pages",
    tags: ["competitor", "comparison", "salsify", "pim", "alternative"],
  },
  {
    id: "comp-5",
    title: "Ommni vs Ultra Commerce",
    description: "Compare Ommni's modern UI and architecture with Ultra Commerce's platform",
    url: "/competitors/ultra-commerce",
    category: "Pages",
    tags: ["competitor", "comparison", "ultra commerce", "comestri", "alternative"],
  },
];

// API Documentation Data
export const apiDocs: SearchResult[] = [
  // Order Management API
  {
    id: "api-order-1",
    title: "Order Management API",
    description: "Complete guide to managing orders programmatically",
    url: "https://docs.ommni.io/docs/order/orders",
    category: "API",
    tags: ["api", "orders", "order management", "documentation"],
  },
  {
    id: "api-order-2",
    title: "Order Schema",
    description: "Order data structure and field definitions",
    url: "https://docs.ommni.io/docs/order/schemas/order",
    category: "API",
    tags: ["api", "orders", "schema", "data structure"],
  },
  {
    id: "api-order-3",
    title: "Create Order",
    description: "Create a new order via API",
    url: "https://docs.ommni.io/docs/order/create-order",
    category: "API",
    tags: ["api", "orders", "create", "post"],
  },
  {
    id: "api-order-4",
    title: "Update Order",
    description: "Update an order by ID",
    url: "https://docs.ommni.io/docs/order/update-order",
    category: "API",
    tags: ["api", "orders", "update", "put", "patch"],
  },
  {
    id: "api-order-5",
    title: "Get Order",
    description: "Get an order by ID",
    url: "https://docs.ommni.io/docs/order/get-order",
    category: "API",
    tags: ["api", "orders", "get", "retrieve"],
  },
  {
    id: "api-order-6",
    title: "Delete Order",
    description: "Delete an order by ID",
    url: "https://docs.ommni.io/docs/order/delete-order",
    category: "API",
    tags: ["api", "orders", "delete", "remove"],
  },
  {
    id: "api-order-7",
    title: "Search Orders",
    description: "Search for orders using filters and queries",
    url: "https://docs.ommni.io/docs/order/search-orders",
    category: "API",
    tags: ["api", "orders", "search", "query", "filter"],
  },
  // Product/PIM API
  {
    id: "api-pim-1",
    title: "Product Management API",
    description: "Manage products, variants, inventory, and catalog data via API",
    url: "https://docs.ommni.io/docs/pim/products",
    category: "API",
    tags: ["api", "products", "pim", "catalog", "inventory"],
  },
  {
    id: "api-pim-2",
    title: "Product Schema",
    description: "Product data structure and field definitions",
    url: "https://docs.ommni.io/docs/pim/schemas/product",
    category: "API",
    tags: ["api", "products", "schema", "data structure"],
  },
  {
    id: "api-pim-3",
    title: "Create Product",
    description: "Add new products to your catalog via API",
    url: "https://docs.ommni.io/docs/pim/create-product",
    category: "API",
    tags: ["api", "products", "create", "post", "catalog"],
  },
  {
    id: "api-pim-4",
    title: "Update Product",
    description: "Update product details by SKU or ID",
    url: "https://docs.ommni.io/docs/pim/update-product",
    category: "API",
    tags: ["api", "products", "update", "put", "patch"],
  },
  {
    id: "api-pim-5",
    title: "Get Product",
    description: "Retrieve product by identifier",
    url: "https://docs.ommni.io/docs/pim/get-product",
    category: "API",
    tags: ["api", "products", "get", "retrieve", "sku"],
  },
  {
    id: "api-pim-6",
    title: "Delete Product",
    description: "Delete product by identifier",
    url: "https://docs.ommni.io/docs/pim/delete-product",
    category: "API",
    tags: ["api", "products", "delete", "remove"],
  },
  {
    id: "api-pim-7",
    title: "Search Products",
    description: "Search and filter products in your catalog",
    url: "https://docs.ommni.io/docs/pim/search-products",
    category: "API",
    tags: ["api", "products", "search", "query", "filter"],
  },
  {
    id: "api-pim-8",
    title: "Attribute Schema",
    description: "Product attribute definitions and configurations",
    url: "https://docs.ommni.io/docs/pim/schemas/attribute",
    category: "API",
    tags: ["api", "attributes", "schema", "product data", "pim"],
  },
  // Inventory API
  {
    id: "api-inventory-1",
    title: "Inventory Management API",
    description: "Manage inventory levels and stock across locations via API",
    url: "https://docs.ommni.io/docs/inventory/inventories",
    category: "API",
    tags: ["api", "inventory", "stock", "warehouse", "management"],
  },
  {
    id: "api-inventory-2",
    title: "Inventory Schema",
    description: "Inventory data structure and field definitions",
    url: "https://docs.ommni.io/docs/inventory/schemas/inventory",
    category: "API",
    tags: ["api", "inventory", "schema", "data structure"],
  },
  {
    id: "api-inventory-3",
    title: "Store Schema",
    description: "Store location data structure and configuration",
    url: "https://docs.ommni.io/docs/inventory/schemas/store",
    category: "API",
    tags: ["api", "store", "location", "schema", "warehouse"],
  },
  {
    id: "api-inventory-4",
    title: "Create Inventory",
    description: "Create new inventory records via API",
    url: "https://docs.ommni.io/docs/inventory/create-inventory",
    category: "API",
    tags: ["api", "inventory", "create", "post", "stock"],
  },
  {
    id: "api-inventory-5",
    title: "Update Inventory",
    description: "Update inventory by identifier",
    url: "https://docs.ommni.io/docs/inventory/update-inventory",
    category: "API",
    tags: ["api", "inventory", "update", "put", "patch"],
  },
  {
    id: "api-inventory-6",
    title: "Get Inventory",
    description: "Retrieve inventory by identifier",
    url: "https://docs.ommni.io/docs/inventory/get-inventory",
    category: "API",
    tags: ["api", "inventory", "get", "retrieve"],
  },
  {
    id: "api-inventory-7",
    title: "Delete Inventory",
    description: "Delete inventory by identifier",
    url: "https://docs.ommni.io/docs/inventory/delete-inventory",
    category: "API",
    tags: ["api", "inventory", "delete", "remove"],
  },
  {
    id: "api-inventory-8",
    title: "Search Inventories",
    description: "Search and filter inventory records",
    url: "https://docs.ommni.io/docs/inventory/search-inventories",
    category: "API",
    tags: ["api", "inventory", "search", "query", "filter"],
  },
  // Offer/Pricing API
  {
    id: "api-offer-1",
    title: "Offer & Pricing API",
    description: "Manage pricing and offers for items via API",
    url: "https://docs.ommni.io/docs/offer/items",
    category: "API",
    tags: ["api", "offer", "pricing", "items", "management"],
  },
  {
    id: "api-offer-2",
    title: "Item Schema",
    description: "Offer item data structure and pricing definitions",
    url: "https://docs.ommni.io/docs/offer/schemas/item",
    category: "API",
    tags: ["api", "offer", "item", "schema", "pricing"],
  },
  {
    id: "api-offer-3",
    title: "Create an Item",
    description: "Create new offer items with pricing via API",
    url: "https://docs.ommni.io/docs/offer/create-an-item",
    category: "API",
    tags: ["api", "offer", "create", "post", "pricing"],
  },
  {
    id: "api-offer-4",
    title: "Update an Item",
    description: "Update offer item by identifier",
    url: "https://docs.ommni.io/docs/offer/update-an-item",
    category: "API",
    tags: ["api", "offer", "update", "put", "patch"],
  },
  {
    id: "api-offer-5",
    title: "Get an Item",
    description: "Retrieve offer item by identifier",
    url: "https://docs.ommni.io/docs/offer/get-an-item",
    category: "API",
    tags: ["api", "offer", "get", "retrieve"],
  },
  {
    id: "api-offer-6",
    title: "Delete an Item",
    description: "Delete offer item by identifier",
    url: "https://docs.ommni.io/docs/offer/delete-an-item",
    category: "API",
    tags: ["api", "offer", "delete", "remove"],
  },
  {
    id: "api-offer-7",
    title: "Retrieve a List of Items",
    description: "Search and filter offer items",
    url: "https://docs.ommni.io/docs/offer/retrieve-a-list-of-items",
    category: "API",
    tags: ["api", "offer", "search", "query", "filter", "list"],
  },
  // DAM (Digital Asset Management) API
  {
    id: "api-dam-1",
    title: "Digital Asset Management API",
    description: "Manage digital assets, images, and media files via API",
    url: "https://docs.ommni.io/docs/dam/assets",
    category: "API",
    tags: ["api", "dam", "assets", "media", "images"],
  },
  {
    id: "api-dam-2",
    title: "Asset Schema",
    description: "Digital asset data structure and metadata definitions",
    url: "https://docs.ommni.io/docs/dam/schemas/asset",
    category: "API",
    tags: ["api", "dam", "asset", "schema", "metadata"],
  },
  {
    id: "api-dam-3",
    title: "Upload Asset",
    description: "Upload digital assets with pre-signed URLs",
    url: "https://docs.ommni.io/docs/dam/upload-asset",
    category: "API",
    tags: ["api", "dam", "upload", "post", "assets"],
  },
  {
    id: "api-dam-4",
    title: "Update Asset",
    description: "Update asset entry by ID",
    url: "https://docs.ommni.io/docs/dam/update-asset",
    category: "API",
    tags: ["api", "dam", "update", "put", "patch"],
  },
  {
    id: "api-dam-5",
    title: "Get Asset",
    description: "Retrieve asset entry by ID",
    url: "https://docs.ommni.io/docs/dam/get-asset",
    category: "API",
    tags: ["api", "dam", "get", "retrieve"],
  },
  {
    id: "api-dam-6",
    title: "Delete Asset",
    description: "Delete asset entry by ID",
    url: "https://docs.ommni.io/docs/dam/delete-asset",
    category: "API",
    tags: ["api", "dam", "delete", "remove"],
  },
  {
    id: "api-dam-7",
    title: "Search Assets",
    description: "Search and filter digital assets",
    url: "https://docs.ommni.io/docs/dam/search-assets",
    category: "API",
    tags: ["api", "dam", "search", "query", "filter"],
  },
  // DAM Folders
  {
    id: "api-dam-folder-1",
    title: "DAM Folders",
    description: "Organize assets with folder structure",
    url: "https://docs.ommni.io/docs/dam/folders",
    category: "API",
    tags: ["api", "dam", "folders", "organization"],
  },
  {
    id: "api-dam-folder-2",
    title: "Folder Schema",
    description: "Folder data structure for organizing assets",
    url: "https://docs.ommni.io/docs/dam/schemas/folder",
    category: "API",
    tags: ["api", "dam", "folder", "schema"],
  },
  {
    id: "api-dam-folder-3",
    title: "Create Folder",
    description: "Create a new folder in DAM",
    url: "https://docs.ommni.io/docs/dam/create-a-new-folder",
    category: "API",
    tags: ["api", "dam", "folder", "create"],
  },
  {
    id: "api-dam-folder-4",
    title: "Get Folder",
    description: "Retrieve folder details",
    url: "https://docs.ommni.io/docs/dam/get-a-folder",
    category: "API",
    tags: ["api", "dam", "folder", "get"],
  },
  {
    id: "api-dam-folder-5",
    title: "Update Folder",
    description: "Update folder information",
    url: "https://docs.ommni.io/docs/dam/update-a-folder",
    category: "API",
    tags: ["api", "dam", "folder", "update"],
  },
  {
    id: "api-dam-folder-6",
    title: "Delete Folder",
    description: "Delete a folder from DAM",
    url: "https://docs.ommni.io/docs/dam/delete-a-folder",
    category: "API",
    tags: ["api", "dam", "folder", "delete"],
  },
  {
    id: "api-dam-folder-7",
    title: "Search Folders",
    description: "Search for folders in DAM",
    url: "https://docs.ommni.io/docs/dam/search-for-folders",
    category: "API",
    tags: ["api", "dam", "folder", "search"],
  },
  // DAM Storage Providers
  {
    id: "api-dam-provider-1",
    title: "Storage Providers",
    description: "Manage storage provider configurations",
    url: "https://docs.ommni.io/docs/dam/providers",
    category: "API",
    tags: ["api", "dam", "storage", "providers"],
  },
  {
    id: "api-dam-provider-2",
    title: "Provider Schema",
    description: "Storage provider configuration structure",
    url: "https://docs.ommni.io/docs/dam/schemas/provider",
    category: "API",
    tags: ["api", "dam", "provider", "schema"],
  },
  {
    id: "api-dam-provider-3",
    title: "Create Storage Provider",
    description: "Configure a new storage provider",
    url: "https://docs.ommni.io/docs/dam/create-storage-provider",
    category: "API",
    tags: ["api", "dam", "provider", "create"],
  },
  {
    id: "api-dam-provider-4",
    title: "Get Storage Provider",
    description: "Retrieve provider configuration",
    url: "https://docs.ommni.io/docs/dam/get-storage-provider",
    category: "API",
    tags: ["api", "dam", "provider", "get"],
  },
  {
    id: "api-dam-provider-5",
    title: "Update Storage Provider",
    description: "Update provider settings",
    url: "https://docs.ommni.io/docs/dam/update-storage-provider",
    category: "API",
    tags: ["api", "dam", "provider", "update"],
  },
  {
    id: "api-dam-provider-6",
    title: "Delete Storage Provider",
    description: "Remove a storage provider",
    url: "https://docs.ommni.io/docs/dam/delete-storage-provider",
    category: "API",
    tags: ["api", "dam", "provider", "delete"],
  },
  {
    id: "api-dam-provider-7",
    title: "Search Storage Providers",
    description: "Search storage provider configurations",
    url: "https://docs.ommni.io/docs/dam/search-storage-providers",
    category: "API",
    tags: ["api", "dam", "provider", "search"],
  },
  // Inventory Sources
  {
    id: "api-inventory-source-1",
    title: "Inventory Sources",
    description: "Manage inventory source locations",
    url: "https://docs.ommni.io/docs/inventory/sources",
    category: "API",
    tags: ["api", "inventory", "source", "warehouse"],
  },
  {
    id: "api-inventory-source-2",
    title: "Source Schema",
    description: "Inventory source data structure",
    url: "https://docs.ommni.io/docs/inventory/schemas/source",
    category: "API",
    tags: ["api", "inventory", "source", "schema"],
  },
  {
    id: "api-inventory-source-3",
    title: "Create Source",
    description: "Create new inventory source",
    url: "https://docs.ommni.io/docs/inventory/create-source",
    category: "API",
    tags: ["api", "inventory", "source", "create"],
  },
  {
    id: "api-inventory-source-4",
    title: "Get Source",
    description: "Retrieve inventory source details",
    url: "https://docs.ommni.io/docs/inventory/get-source",
    category: "API",
    tags: ["api", "inventory", "source", "get"],
  },
  {
    id: "api-inventory-source-5",
    title: "Update Source",
    description: "Update inventory source",
    url: "https://docs.ommni.io/docs/inventory/update-source",
    category: "API",
    tags: ["api", "inventory", "source", "update"],
  },
  {
    id: "api-inventory-source-6",
    title: "Delete Source",
    description: "Delete inventory source",
    url: "https://docs.ommni.io/docs/inventory/delete-source",
    category: "API",
    tags: ["api", "inventory", "source", "delete"],
  },
  {
    id: "api-inventory-source-7",
    title: "Search Sources",
    description: "Search inventory sources",
    url: "https://docs.ommni.io/docs/inventory/search-sources",
    category: "API",
    tags: ["api", "inventory", "source", "search"],
  },
  // Inventory Stores
  {
    id: "api-inventory-store-1",
    title: "Inventory Stores",
    description: "Manage store locations and inventory",
    url: "https://docs.ommni.io/docs/inventory/stores",
    category: "API",
    tags: ["api", "inventory", "store", "location"],
  },
  {
    id: "api-inventory-store-3",
    title: "Create Store",
    description: "Create new store location",
    url: "https://docs.ommni.io/docs/inventory/create-store",
    category: "API",
    tags: ["api", "inventory", "store", "create"],
  },
  {
    id: "api-inventory-store-4",
    title: "Get Store",
    description: "Retrieve store details",
    url: "https://docs.ommni.io/docs/inventory/get-store",
    category: "API",
    tags: ["api", "inventory", "store", "get"],
  },
  {
    id: "api-inventory-store-5",
    title: "Update Store",
    description: "Update store information",
    url: "https://docs.ommni.io/docs/inventory/update-store",
    category: "API",
    tags: ["api", "inventory", "store", "update"],
  },
  {
    id: "api-inventory-store-6",
    title: "Delete Store",
    description: "Delete store location",
    url: "https://docs.ommni.io/docs/inventory/delete-store",
    category: "API",
    tags: ["api", "inventory", "store", "delete"],
  },
  {
    id: "api-inventory-store-7",
    title: "Search Stores",
    description: "Search store locations",
    url: "https://docs.ommni.io/docs/inventory/search-stores",
    category: "API",
    tags: ["api", "inventory", "store", "search"],
  },
  // Offer Price Lists
  {
    id: "api-offer-pricelist-1",
    title: "Price Lists",
    description: "Manage pricing lists for offers",
    url: "https://docs.ommni.io/docs/offer/price-lists",
    category: "API",
    tags: ["api", "offer", "pricing", "price list"],
  },
  {
    id: "api-offer-pricelist-2",
    title: "Price List Schema",
    description: "Price list data structure",
    url: "https://docs.ommni.io/docs/offer/schemas/pricelist",
    category: "API",
    tags: ["api", "offer", "price list", "schema"],
  },
  {
    id: "api-offer-pricelist-3",
    title: "Create Price List",
    description: "Create a new price list",
    url: "https://docs.ommni.io/docs/offer/create-a-price-list",
    category: "API",
    tags: ["api", "offer", "price list", "create"],
  },
  {
    id: "api-offer-pricelist-4",
    title: "Get Price List",
    description: "Retrieve price list details",
    url: "https://docs.ommni.io/docs/offer/get-a-price-list",
    category: "API",
    tags: ["api", "offer", "price list", "get"],
  },
  {
    id: "api-offer-pricelist-5",
    title: "Update Price List",
    description: "Update price list configuration",
    url: "https://docs.ommni.io/docs/offer/update-a-price-list",
    category: "API",
    tags: ["api", "offer", "price list", "update"],
  },
  {
    id: "api-offer-pricelist-6",
    title: "Delete Price List",
    description: "Delete a price list",
    url: "https://docs.ommni.io/docs/offer/delete-a-price-list",
    category: "API",
    tags: ["api", "offer", "price list", "delete"],
  },
  {
    id: "api-offer-pricelist-7",
    title: "List Price Lists",
    description: "Retrieve all price lists",
    url: "https://docs.ommni.io/docs/offer/retrieve-a-list-of-price-lists",
    category: "API",
    tags: ["api", "offer", "price list", "search"],
  },
  // PIM - Brands
  {
    id: "api-pim-brand-1",
    title: "Product Brands",
    description: "Manage product brands in catalog",
    url: "https://docs.ommni.io/docs/pim/brands",
    category: "API",
    tags: ["api", "pim", "brands", "catalog"],
  },
  {
    id: "api-pim-brand-2",
    title: "Brand Schema",
    description: "Brand data structure definition",
    url: "https://docs.ommni.io/docs/pim/schemas/brand",
    category: "API",
    tags: ["api", "pim", "brand", "schema"],
  },
  {
    id: "api-pim-brand-3",
    title: "Create Brand",
    description: "Add new brand to catalog",
    url: "https://docs.ommni.io/docs/pim/create-brand",
    category: "API",
    tags: ["api", "pim", "brand", "create"],
  },
  {
    id: "api-pim-brand-4",
    title: "Get Brand",
    description: "Retrieve brand details",
    url: "https://docs.ommni.io/docs/pim/get-brand",
    category: "API",
    tags: ["api", "pim", "brand", "get"],
  },
  {
    id: "api-pim-brand-5",
    title: "Update Brand",
    description: "Update brand information",
    url: "https://docs.ommni.io/docs/pim/update-brand",
    category: "API",
    tags: ["api", "pim", "brand", "update"],
  },
  {
    id: "api-pim-brand-6",
    title: "Delete Brand",
    description: "Remove brand from catalog",
    url: "https://docs.ommni.io/docs/pim/delete-brand",
    category: "API",
    tags: ["api", "pim", "brand", "delete"],
  },
  {
    id: "api-pim-brand-7",
    title: "Search Brands",
    description: "Search and filter brands",
    url: "https://docs.ommni.io/docs/pim/search-brands",
    category: "API",
    tags: ["api", "pim", "brand", "search"],
  },
  // PIM - Catalogs
  {
    id: "api-pim-catalog-1",
    title: "Product Catalogs",
    description: "Manage product catalogs",
    url: "https://docs.ommni.io/docs/pim/catalogs",
    category: "API",
    tags: ["api", "pim", "catalogs", "products"],
  },
  {
    id: "api-pim-catalog-2",
    title: "Catalog Schema",
    description: "Catalog data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/catalog",
    category: "API",
    tags: ["api", "pim", "catalog", "schema"],
  },
  {
    id: "api-pim-catalog-3",
    title: "Create Catalog",
    description: "Create new product catalog",
    url: "https://docs.ommni.io/docs/pim/create-catalog",
    category: "API",
    tags: ["api", "pim", "catalog", "create"],
  },
  {
    id: "api-pim-catalog-4",
    title: "Get Catalog",
    description: "Retrieve catalog details",
    url: "https://docs.ommni.io/docs/pim/get-catalog",
    category: "API",
    tags: ["api", "pim", "catalog", "get"],
  },
  {
    id: "api-pim-catalog-5",
    title: "Update Catalog",
    description: "Update catalog configuration",
    url: "https://docs.ommni.io/docs/pim/update-catalog",
    category: "API",
    tags: ["api", "pim", "catalog", "update"],
  },
  {
    id: "api-pim-catalog-6",
    title: "Delete Catalog",
    description: "Delete product catalog",
    url: "https://docs.ommni.io/docs/pim/delete-catalog",
    category: "API",
    tags: ["api", "pim", "catalog", "delete"],
  },
  {
    id: "api-pim-catalog-7",
    title: "Search Catalogs",
    description: "Search product catalogs",
    url: "https://docs.ommni.io/docs/pim/search-catalogs",
    category: "API",
    tags: ["api", "pim", "catalog", "search"],
  },
  // PIM - Categories
  {
    id: "api-pim-category-1",
    title: "Product Categories",
    description: "Manage product categories and hierarchy",
    url: "https://docs.ommni.io/docs/pim/category",
    category: "API",
    tags: ["api", "pim", "categories", "taxonomy"],
  },
  {
    id: "api-pim-category-2",
    title: "Category Schema",
    description: "Category data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/category",
    category: "API",
    tags: ["api", "pim", "category", "schema"],
  },
  {
    id: "api-pim-category-3",
    title: "Create Category",
    description: "Create product category",
    url: "https://docs.ommni.io/docs/pim/create-category",
    category: "API",
    tags: ["api", "pim", "category", "create"],
  },
  {
    id: "api-pim-category-4",
    title: "Get Category",
    description: "Retrieve category details",
    url: "https://docs.ommni.io/docs/pim/get-category",
    category: "API",
    tags: ["api", "pim", "category", "get"],
  },
  {
    id: "api-pim-category-5",
    title: "Update Category",
    description: "Update category information",
    url: "https://docs.ommni.io/docs/pim/update-category",
    category: "API",
    tags: ["api", "pim", "category", "update"],
  },
  {
    id: "api-pim-category-6",
    title: "Delete Category",
    description: "Delete product category",
    url: "https://docs.ommni.io/docs/pim/delete-category",
    category: "API",
    tags: ["api", "pim", "category", "delete"],
  },
  {
    id: "api-pim-category-7",
    title: "Search Categories",
    description: "Search product categories",
    url: "https://docs.ommni.io/docs/pim/search-categories",
    category: "API",
    tags: ["api", "pim", "category", "search"],
  },
  // PIM - Category Assets
  {
    id: "api-pim-category-asset-1",
    title: "Category Assets",
    description: "Manage assets for categories",
    url: "https://docs.ommni.io/docs/pim/category-assets",
    category: "API",
    tags: ["api", "pim", "category", "assets"],
  },
  {
    id: "api-pim-category-asset-2",
    title: "Category Asset Schema",
    description: "Category asset data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/categoryasset",
    category: "API",
    tags: ["api", "pim", "category asset", "schema"],
  },
  {
    id: "api-pim-category-asset-3",
    title: "Create Category Asset",
    description: "Add asset to category",
    url: "https://docs.ommni.io/docs/pim/create-category-asset",
    category: "API",
    tags: ["api", "pim", "category asset", "create"],
  },
  {
    id: "api-pim-category-asset-4",
    title: "Get Category Asset",
    description: "Retrieve category asset",
    url: "https://docs.ommni.io/docs/pim/get-category-asset",
    category: "API",
    tags: ["api", "pim", "category asset", "get"],
  },
  {
    id: "api-pim-category-asset-5",
    title: "Update Category Asset",
    description: "Update category asset",
    url: "https://docs.ommni.io/docs/pim/update-category-asset",
    category: "API",
    tags: ["api", "pim", "category asset", "update"],
  },
  {
    id: "api-pim-category-asset-6",
    title: "Delete Category Asset",
    description: "Remove asset from category",
    url: "https://docs.ommni.io/docs/pim/delete-category-asset",
    category: "API",
    tags: ["api", "pim", "category asset", "delete"],
  },
  {
    id: "api-pim-category-asset-7",
    title: "Search Category Assets",
    description: "Search category assets",
    url: "https://docs.ommni.io/docs/pim/search-category-assets",
    category: "API",
    tags: ["api", "pim", "category asset", "search"],
  },
  // PIM - Category Sets
  {
    id: "api-pim-category-set-1",
    title: "Category Sets",
    description: "Manage category set collections",
    url: "https://docs.ommni.io/docs/pim/category-set",
    category: "API",
    tags: ["api", "pim", "category set", "collections"],
  },
  {
    id: "api-pim-category-set-2",
    title: "Category Set Schema",
    description: "Category set data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/categoryset",
    category: "API",
    tags: ["api", "pim", "category set", "schema"],
  },
  {
    id: "api-pim-category-set-3",
    title: "Create Category Set",
    description: "Create new category set",
    url: "https://docs.ommni.io/docs/pim/create-category-set",
    category: "API",
    tags: ["api", "pim", "category set", "create"],
  },
  {
    id: "api-pim-category-set-4",
    title: "Get Category Set",
    description: "Retrieve category set details",
    url: "https://docs.ommni.io/docs/pim/get-category-set",
    category: "API",
    tags: ["api", "pim", "category set", "get"],
  },
  {
    id: "api-pim-category-set-5",
    title: "Update Category Set",
    description: "Update category set",
    url: "https://docs.ommni.io/docs/pim/update-category-set",
    category: "API",
    tags: ["api", "pim", "category set", "update"],
  },
  {
    id: "api-pim-category-set-6",
    title: "Delete Category Set",
    description: "Delete category set",
    url: "https://docs.ommni.io/docs/pim/delete-category-set",
    category: "API",
    tags: ["api", "pim", "category set", "delete"],
  },
  {
    id: "api-pim-category-set-7",
    title: "Search Category Sets",
    description: "Search category sets",
    url: "https://docs.ommni.io/docs/pim/search-category-set",
    category: "API",
    tags: ["api", "pim", "category set", "search"],
  },
  // PIM - Attribute Groups
  {
    id: "api-pim-attr-group-1",
    title: "Attribute Groups",
    description: "Organize attributes into groups",
    url: "https://docs.ommni.io/docs/pim/attribute-group",
    category: "API",
    tags: ["api", "pim", "attribute groups", "organization"],
  },
  {
    id: "api-pim-attr-group-2",
    title: "Attribute Group Schema",
    description: "Attribute group data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/attributegroup",
    category: "API",
    tags: ["api", "pim", "attribute group", "schema"],
  },
  {
    id: "api-pim-attr-group-3",
    title: "Create Attribute Group",
    description: "Create new attribute group",
    url: "https://docs.ommni.io/docs/pim/create-attribute-group",
    category: "API",
    tags: ["api", "pim", "attribute group", "create"],
  },
  {
    id: "api-pim-attr-group-4",
    title: "Get Attribute Group",
    description: "Retrieve attribute group",
    url: "https://docs.ommni.io/docs/pim/get-attribute-group",
    category: "API",
    tags: ["api", "pim", "attribute group", "get"],
  },
  {
    id: "api-pim-attr-group-5",
    title: "Update Attribute Group",
    description: "Update attribute group",
    url: "https://docs.ommni.io/docs/pim/update-attribute-group",
    category: "API",
    tags: ["api", "pim", "attribute group", "update"],
  },
  {
    id: "api-pim-attr-group-6",
    title: "Delete Attribute Group",
    description: "Delete attribute group",
    url: "https://docs.ommni.io/docs/pim/delete-attribute-group",
    category: "API",
    tags: ["api", "pim", "attribute group", "delete"],
  },
  {
    id: "api-pim-attr-group-7",
    title: "Search Attribute Groups",
    description: "Search attribute groups",
    url: "https://docs.ommni.io/docs/pim/search-attribute-groups",
    category: "API",
    tags: ["api", "pim", "attribute group", "search"],
  },
  // PIM - Product Families
  {
    id: "api-pim-family-1",
    title: "Product Families",
    description: "Manage product family structures",
    url: "https://docs.ommni.io/docs/pim/product-family",
    category: "API",
    tags: ["api", "pim", "product family", "structure"],
  },
  {
    id: "api-pim-family-2",
    title: "Product Family Schema",
    description: "Product family data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/productfamily",
    category: "API",
    tags: ["api", "pim", "product family", "schema"],
  },
  {
    id: "api-pim-family-3",
    title: "Create Product Family",
    description: "Create new product family",
    url: "https://docs.ommni.io/docs/pim/create-product-family",
    category: "API",
    tags: ["api", "pim", "product family", "create"],
  },
  {
    id: "api-pim-family-4",
    title: "Get Product Family",
    description: "Retrieve product family",
    url: "https://docs.ommni.io/docs/pim/get-product-family",
    category: "API",
    tags: ["api", "pim", "product family", "get"],
  },
  {
    id: "api-pim-family-5",
    title: "Update Product Family",
    description: "Update product family",
    url: "https://docs.ommni.io/docs/pim/update-product-family",
    category: "API",
    tags: ["api", "pim", "product family", "update"],
  },
  {
    id: "api-pim-family-6",
    title: "Delete Product Family",
    description: "Delete product family",
    url: "https://docs.ommni.io/docs/pim/delete-product-family",
    category: "API",
    tags: ["api", "pim", "product family", "delete"],
  },
  {
    id: "api-pim-family-7",
    title: "Search Product Families",
    description: "Search product families",
    url: "https://docs.ommni.io/docs/pim/search-product-families",
    category: "API",
    tags: ["api", "pim", "product family", "search"],
  },
  // PIM - Product Classes
  {
    id: "api-pim-class-1",
    title: "Product Classes",
    description: "Manage product classifications",
    url: "https://docs.ommni.io/docs/pim/product-class",
    category: "API",
    tags: ["api", "pim", "product class", "classification"],
  },
  {
    id: "api-pim-class-2",
    title: "Product Class Schema",
    description: "Product class data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/productclass",
    category: "API",
    tags: ["api", "pim", "product class", "schema"],
  },
  {
    id: "api-pim-class-3",
    title: "Create Product Class",
    description: "Create new product class",
    url: "https://docs.ommni.io/docs/pim/create-product-class",
    category: "API",
    tags: ["api", "pim", "product class", "create"],
  },
  {
    id: "api-pim-class-4",
    title: "Get Product Class",
    description: "Retrieve product class",
    url: "https://docs.ommni.io/docs/pim/get-product-class",
    category: "API",
    tags: ["api", "pim", "product class", "get"],
  },
  {
    id: "api-pim-class-5",
    title: "Update Product Class",
    description: "Update product class",
    url: "https://docs.ommni.io/docs/pim/update-product-class",
    category: "API",
    tags: ["api", "pim", "product class", "update"],
  },
  {
    id: "api-pim-class-6",
    title: "Delete Product Class",
    description: "Delete product class",
    url: "https://docs.ommni.io/docs/pim/delete-product-class",
    category: "API",
    tags: ["api", "pim", "product class", "delete"],
  },
  {
    id: "api-pim-class-7",
    title: "Search Product Classes",
    description: "Search product classes",
    url: "https://docs.ommni.io/docs/pim/search-product-classes",
    category: "API",
    tags: ["api", "pim", "product class", "search"],
  },
  // PIM - Product Assets
  {
    id: "api-pim-product-asset-1",
    title: "Product Assets",
    description: "Manage product images and media",
    url: "https://docs.ommni.io/docs/pim/product-assets",
    category: "API",
    tags: ["api", "pim", "product assets", "images"],
  },
  {
    id: "api-pim-product-asset-2",
    title: "Product Asset Schema",
    description: "Product asset data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/productasset",
    category: "API",
    tags: ["api", "pim", "product asset", "schema"],
  },
  {
    id: "api-pim-product-asset-3",
    title: "Create Product Asset",
    description: "Add asset to product",
    url: "https://docs.ommni.io/docs/pim/create-product-asset",
    category: "API",
    tags: ["api", "pim", "product asset", "create"],
  },
  {
    id: "api-pim-product-asset-4",
    title: "Retrieve Product Asset",
    description: "Get product asset details",
    url: "https://docs.ommni.io/docs/pim/retrieve-product-asset",
    category: "API",
    tags: ["api", "pim", "product asset", "get"],
  },
  {
    id: "api-pim-product-asset-5",
    title: "Update Product Asset",
    description: "Update product asset",
    url: "https://docs.ommni.io/docs/pim/update-product-asset",
    category: "API",
    tags: ["api", "pim", "product asset", "update"],
  },
  {
    id: "api-pim-product-asset-6",
    title: "Delete Product Asset",
    description: "Remove asset from product",
    url: "https://docs.ommni.io/docs/pim/delete-product-asset",
    category: "API",
    tags: ["api", "pim", "product asset", "delete"],
  },
  {
    id: "api-pim-product-asset-7",
    title: "Search Product Assets",
    description: "Search product assets",
    url: "https://docs.ommni.io/docs/pim/search-product-assets",
    category: "API",
    tags: ["api", "pim", "product asset", "search"],
  },
  // PIM - Options and Option Lists
  {
    id: "api-pim-options-1",
    title: "Product Options",
    description: "Manage product option values",
    url: "https://docs.ommni.io/docs/pim/options",
    category: "API",
    tags: ["api", "pim", "options", "variants"],
  },
  {
    id: "api-pim-options-2",
    title: "Option Schema",
    description: "Option value data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/option",
    category: "API",
    tags: ["api", "pim", "option", "schema"],
  },
  {
    id: "api-pim-options-3",
    title: "Add Option",
    description: "Add option value to list",
    url: "https://docs.ommni.io/docs/pim/add-option",
    category: "API",
    tags: ["api", "pim", "option", "create"],
  },
  {
    id: "api-pim-options-4",
    title: "Get Option",
    description: "Retrieve option details",
    url: "https://docs.ommni.io/docs/pim/get-option",
    category: "API",
    tags: ["api", "pim", "option", "get"],
  },
  {
    id: "api-pim-options-5",
    title: "Update Option",
    description: "Update option value",
    url: "https://docs.ommni.io/docs/pim/update-option",
    category: "API",
    tags: ["api", "pim", "option", "update"],
  },
  {
    id: "api-pim-options-6",
    title: "Delete Option",
    description: "Remove option value",
    url: "https://docs.ommni.io/docs/pim/delete-option",
    category: "API",
    tags: ["api", "pim", "option", "delete"],
  },
  {
    id: "api-pim-options-7",
    title: "Search Options",
    description: "Search option values",
    url: "https://docs.ommni.io/docs/pim/search-options",
    category: "API",
    tags: ["api", "pim", "option", "search"],
  },
  {
    id: "api-pim-optionlist-1",
    title: "Options Lists",
    description: "Manage option list collections",
    url: "https://docs.ommni.io/docs/pim/option-lists",
    category: "API",
    tags: ["api", "pim", "option lists", "collections"],
  },
  {
    id: "api-pim-optionlist-2",
    title: "Options List Schema",
    description: "Options list data structure",
    url: "https://docs.ommni.io/docs/pim/schemas/optionslist",
    category: "API",
    tags: ["api", "pim", "options list", "schema"],
  },
  {
    id: "api-pim-optionlist-3",
    title: "Create Options List",
    description: "Create new options list",
    url: "https://docs.ommni.io/docs/pim/create-options-list",
    category: "API",
    tags: ["api", "pim", "options list", "create"],
  },
  {
    id: "api-pim-optionlist-4",
    title: "Get Options List",
    description: "Retrieve options list",
    url: "https://docs.ommni.io/docs/pim/get-options-list",
    category: "API",
    tags: ["api", "pim", "options list", "get"],
  },
  {
    id: "api-pim-optionlist-5",
    title: "Update Options List",
    description: "Update options list",
    url: "https://docs.ommni.io/docs/pim/update-options-list",
    category: "API",
    tags: ["api", "pim", "options list", "update"],
  },
  {
    id: "api-pim-optionlist-6",
    title: "Delete Options List",
    description: "Delete options list",
    url: "https://docs.ommni.io/docs/pim/delete-options-list",
    category: "API",
    tags: ["api", "pim", "options list", "delete"],
  },
  {
    id: "api-pim-optionlist-7",
    title: "Search Options Lists",
    description: "Search options lists",
    url: "https://docs.ommni.io/docs/pim/search-options-list",
    category: "API",
    tags: ["api", "pim", "options list", "search"],
  },
  // PIM - Attributes (already covered but ensuring completeness)
  {
    id: "api-pim-attributes-1",
    title: "Product Attributes",
    description: "Define and manage product attributes",
    url: "https://docs.ommni.io/docs/pim/attributes",
    category: "API",
    tags: ["api", "pim", "attributes", "properties"],
  },
  {
    id: "api-pim-attributes-3",
    title: "Create Attribute",
    description: "Create new product attribute",
    url: "https://docs.ommni.io/docs/pim/create-attribute",
    category: "API",
    tags: ["api", "pim", "attribute", "create"],
  },
  {
    id: "api-pim-attributes-4",
    title: "Get Attribute",
    description: "Retrieve attribute details",
    url: "https://docs.ommni.io/docs/pim/get-attribute",
    category: "API",
    tags: ["api", "pim", "attribute", "get"],
  },
  {
    id: "api-pim-attributes-5",
    title: "Update Attribute",
    description: "Update attribute configuration",
    url: "https://docs.ommni.io/docs/pim/update-attribute",
    category: "API",
    tags: ["api", "pim", "attribute", "update"],
  },
  {
    id: "api-pim-attributes-6",
    title: "Delete Attribute",
    description: "Delete product attribute",
    url: "https://docs.ommni.io/docs/pim/delete-attribute",
    category: "API",
    tags: ["api", "pim", "attribute", "delete"],
  },
  {
    id: "api-pim-attributes-7",
    title: "Search Attributes",
    description: "Search product attributes",
    url: "https://docs.ommni.io/docs/pim/search-attributes",
    category: "API",
    tags: ["api", "pim", "attribute", "search"],
  },
  // Reference & Guides
  {
    id: "api-ref-1",
    title: "API Overview",
    description: "Complete guide to Ommni's API platform",
    url: "https://docs.ommni.io/docs/reference/overview",
    category: "API",
    tags: ["api", "overview", "documentation", "getting started"],
  },
  {
    id: "api-ref-2",
    title: "API Authentication",
    description: "Authentication methods and security best practices",
    url: "https://docs.ommni.io/docs/reference/api-authentication",
    category: "API",
    tags: ["api", "authentication", "security", "auth"],
  },
  {
    id: "api-ref-3",
    title: "Getting Started with Ommni APIs",
    description: "Quick start guide for Ommni API integration",
    url: "https://docs.ommni.io/docs/reference/getting-started-with-ommni-apis",
    category: "API",
    tags: ["api", "getting started", "tutorial", "guide"],
  },
  {
    id: "api-ref-4",
    title: "API Integration Manual",
    description: "Comprehensive integration guide for developers",
    url: "https://docs.ommni.io/docs/guides/Ommni_API_Integration_Manual",
    category: "API",
    tags: ["api", "integration", "manual", "developers"],
  },
  {
    id: "api-ref-5",
    title: "Attributes Reference Guide",
    description: "Complete reference for product attributes",
    url: "https://docs.ommni.io/docs/guides/Attributes_Reference_Guide",
    category: "API",
    tags: ["api", "attributes", "reference", "guide"],
  },
];

// Combined search data
export const allSearchData: SearchResult[] = [
  ...features,
  ...solutions,
  ...pages,
  ...blogPosts,
  ...competitorPages,
  ...apiDocs,
];

export default allSearchData;
